
import React from "react";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { User, Beaker, Building, MessageSquare, MapPin } from "lucide-react";
import { Reagent } from "@/types/reagent.types";

interface OwnerInfoCardProps {
  reagent: Reagent;
}

const OwnerInfoCard: React.FC<OwnerInfoCardProps> = ({ reagent }) => {
  // Use display_username if available, otherwise fall back to username
  // Handle both potential profile structures (from different queries)
  const displayName = 
    (reagent.profiles && reagent.profiles.display_username) || 
    (reagent.profiles && reagent.profiles.username) || 
    reagent.userName ||
    "Unknown";
  
  // Format URLs to ensure they have the proper protocol
  const formatUrl = (url: string | null): string | null => {
    if (!url) return null;
    
    // Return if already has protocol
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Add https:// prefix
    return `https://${url}`;
  };
  
  // Render the location (city, country) if available
  const renderLocation = () => {
    const city = reagent.city || (reagent.profiles && reagent.profiles.city);
    const country = reagent.country || (reagent.profiles && reagent.profiles.country);
    
    if (city || country) {
      return (
        <div className="flex items-start">
          <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
            <MapPin className="h-5 w-5 text-reagent-blue" />
          </div>
          <div>
            <h3 className="font-medium">Location</h3>
            <p>{[city, country].filter(Boolean).join(", ")}</p>
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-4">Listed by</h2>
      
      <div className="space-y-4">
        <div className="flex items-start">
          <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
            <User className="h-5 w-5 text-reagent-blue" />
          </div>
          <div>
            <h3 className="font-medium">Researcher</h3>
            <p>{displayName}</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
            <Beaker className="h-5 w-5 text-reagent-blue" />
          </div>
          <div>
            <h3 className="font-medium">Lab</h3>
            <p>{(reagent.profiles && reagent.profiles.lab) || reagent.labName || "Not specified"}</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <div className="h-8 w-8 rounded-full bg-reagent-light-blue flex items-center justify-center mt-1 mr-3">
            <Building className="h-5 w-5 text-reagent-blue" />
          </div>
          <div>
            <h3 className="font-medium">Institution</h3>
            <p>{(reagent.profiles && reagent.profiles.university) || reagent.university || "Not specified"}</p>
          </div>
        </div>
        
        {renderLocation()}
      </div>
      
      <Separator className="my-6" />
      
      <div className="space-y-4">
        <Button asChild className="w-full">
          <Link to={`/request-swap/${reagent.id}`}>Request Swap</Link>
        </Button>
        
        <Button asChild variant="outline" className="w-full">
          <Link to={`/message/${reagent.id}`}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Message Owner
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default OwnerInfoCard;
